
import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback, useActionState, useMemo } from "react";
import ReactDOM from 'react-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Button,
  Badge,
  Box,
  Select,
  Divider,
  Drawer,
  TextField,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Autocomplete,
  Paper, Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Snackbar
} from '@mui/material';
import { ArrowDropDown, KeyboardArrowUp, KeyboardArrowDown,  } from '@mui/icons-material';
import { styled } from '@mui/system';

import { EnumUtility, UtilityLibrary, LoggerUtility, ApiManager, ClientType } from '../components/ComponentsLibrary';

import { Close, Info } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import AutorenewIcon from '@mui/icons-material/Autorenew';
// Add these icon imports:
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';


  function useInputImperativeHandle(ref, setText) {
    const inputRef = useRef();
  
    useImperativeHandle(ref, () => ({
      clear: () => {
        if (inputRef.current) {
          setText("");
          //inputRef.current.value = "";
        }
      },
      setValue: (value) => {
        if (inputRef.current) {
          setText(value);
          //inputRef.current.value = value;
        }
      },
      getValue: () => {
        return inputRef.current ? inputRef.current.value : "";
      },
    }), [setText]);
  
    return inputRef;
  }


/**
 * useCharCount Hook
 * Custom Hook for managing character count in text fields
 * @param {string} initialValue - Initial value of the text field
 * @param {number} maxLength - Maximum allowed characters
 * @returns {Object} - Contains text value, character count, and handler
 */
const useCharCount = (initialValue = "", maxLength = 100, onTextChange) => {
  const [text, setText] = useState(initialValue);

  const handleChange = useCallback((e) => {
    const newText = e.target.value;
    if (newText.length <= maxLength) {
      setText(newText);

      if(onTextChange) {
        onTextChange(e);
      }
    }
  }, [maxLength, onTextChange]);

  return {
    text,
    charCount: text.length,
    maxLength,
    handleChange,
    setText
  };
};

/**
 * CharacterCountField Component
 * @param {Object} props - Properties for the component
 * @param {string} props.id - Unique identifier for the field
 * @param {string} [props.initialValue] - Initial value of the text field
 * @param {number} props.maxLength - Maximum allowed characters
 * @param {string} [props.placeholder] - Placeholder for the text field
 */
const TextAreaWithCount = forwardRef(({ id, nameForField = "", labelValue = "", initialValue = "", value = "", maxLength = 100, placeholder = "", isDisabled = false, onTextChange, requiredFieldLabel="" , readOnly=false, showCopyButton = false, copyTooltipText="Copy Text"}, ref) => {
  const { text, charCount, handleChange, setText } = useCharCount(initialValue, maxLength, onTextChange);
  const textFieldRef = useInputImperativeHandle(ref, setText);

  useEffect(() => {
    if (value !== undefined && value !== text) {
      setText(value ?? "");
    }
   }, [value]);
  
  const handleCopyTextClick = async (e) => {
    try {
      await navigator.clipboard.writeText(text);
    }
    catch (error) {

    }
  };
  return (
    <div style={{ width: "100%", height: "100%" }}>
      {showCopyButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '4px' }}>
          <Tooltip
            title={copyTooltipText}
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={handleCopyTextClick}
                disabled={!text}
              >
                <ContentCopyIcon color="ywButtonBlue" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: "100%" }}>
        <TextField
          id={id}
          name={nameForField}
          inputRef={textFieldRef}
          value={text}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          inputProps={{ maxLength }}
          multiline={true}
          rows={3}
          label={labelValue}
          InputLabelProps={{ shrink: true }}
          variant='outlined'
          fullWidth
          style={{ flex: 1 }}
          InputProps ={{readOnly: readOnly}}
        />
        <label>
          <span style={{ width: '10px', display: 'inline-block', textAlign: 'center', color: 'red' }}>
            {requiredFieldLabel}
          </span>
        </label>
      </div>
      <div
        style={{
          textAlign: "left",
          marginTop: "5px",
          fontSize: "14px",
          color: charCount === maxLength ? "red" : "black",
        }}
      >
        {charCount}/{maxLength}
      </div>
    </div>
  );
});


const TextFieldWithIconButton = React.memo(forwardRef(({
  valueForField: externalValue,
  nameForField = "",
  onIconButtonClick,        // Custom button click handler passed from the parent
  onClearButtonClick,        // Custom button click handler passed from the parent to clear the text
  onTextChange,
  textIcon,            // Icon passed from the parent (default is SearchIcon)
  labelForField = "Search", // Label for the textfield
  placeholder = "Search", // Placeholder for the textfield
  initialText = "", // Initial Text value
  isDisabled = false,
  iconsVisibility = {search: true, copy: true, clear: true},
  iconsDisabled = {search: false, copy: false, clear: false},
  iconsTooltipText = {search: 'Search', copy: 'Copy Text', clear: 'Clear Text'},
  requiredFieldLabel = "",
  sizeForControl = "normal",
  disableKeyDown = false,
  removeSpacesOnInput = false,
  sx = {}, // Add sx prop for styling
}, ref) => {

  const [internalValue, setInternalValue] = useState(""); // Internal state for default behavior
  const inputRef = useRef(); // Ref for the TextField input element

  const [mainValidationMessage, setMainValidationMessage] = useState("");

  // Determine whether the field is external or internal
  const isExternal = externalValue !== undefined;

  // Handle input change
  const handleTextChange = (e) => {
  const shouldRemoveSpaces =
    removeSpacesOnInput || /(caseNumber|caseSearch)/i.test(nameForField || "");

  let updatedValue = e?.target?.value ?? "";

  if (shouldRemoveSpaces) {
    updatedValue = String(updatedValue)
      .replace(/\s+/g, "")
      .replace(/-/g, "")
      .replace(/^0+/, "");
  }

  const updatedEvent = shouldRemoveSpaces
    ? {
        ...e,
        target: {
          name: e?.target?.name || nameForField,
          value: updatedValue,
        },
      }
    : e;

  if (onTextChange) {
    onTextChange(updatedEvent);
  } else {
    setInternalValue(updatedValue);
  }
};
  const handleButtonClick = (event) => {
    const searchValue = isExternal ? externalValue : internalValue; // Get value based on mode
    if (searchValue.trim()) {
      if (onIconButtonClick) {
        onIconButtonClick(event); // Calls the parent-provided search handler
      } else {
        // Default search action
      }
    }
  };

  // Handle Clear Icon Click
  const handleClearClick = (e) => {
    if (onClearButtonClick) {
      setInternalValue(""); // Update internal state 
      onClearButtonClick(e); // If parent provided an onClearButtonClick, invoke it      
    } else {
      setInternalValue(""); // Update internal state 
    }
  };

  const handleKeyDown = (event) => {
	  if(disableKeyDown) {
      return;
    }
    if (event.key === "Enter") {
      handleButtonClick();
    }
  };

  // Handle Copy Icon Click
  const handleCopyTextClick = async (e) => {
    try {
      const textToCopy = isExternal ? externalValue : internalValue;
      await navigator.clipboard.writeText(textToCopy);
    }
    catch (error) {

    }
  };

  const clearAllText = () => {
    externalValue = "";
    setInternalValue("");
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getTextValue: () => (isExternal ? externalValue : internalValue), // Get current value
    setTextValue: (newValue) =>
      isExternal ? null : setInternalValue(newValue), // Set value (only in internal mode)
    setValidationMessage: (newValue) =>
      setMainValidationMessage(newValue), // Set validation message (only in internal mode)
    clearAllText,
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <span>
        <div className={`textFieldWithIcon ${requiredFieldLabel !== "" ? "textFieldWithIconRequired" : ""}`}>
          <TextField
            size={sizeForControl}
            label={labelForField}
            name={nameForField}
            variant="outlined"
            value={isExternal ? externalValue : internalValue}
            disabled={isDisabled}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {iconsVisibility.search &&
                    <Tooltip title={iconsTooltipText.search} arrow disableHoverListener={isDisabled || iconsDisabled.search} disableFocusListener={isDisabled || iconsDisabled.search}>
                      <span>
                        <IconButton onClick={handleButtonClick} style={{ padding: 10 }} disabled={isDisabled || iconsDisabled.search}>
                          {textIcon || <SearchIcon color="ywButtonBlue" />} {/* Default to SearchIcon if no icon is provided */}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  {iconsVisibility.copy &&
                    <Tooltip title={iconsTooltipText.copy} arrow disableHoverListener={iconsDisabled.copy} disableFocusListener={ iconsDisabled.copy}>
                      <span>
                        <IconButton onClick={handleCopyTextClick} disabled={ iconsDisabled.copy}>
                          <ContentCopyIcon color="ywButtonBlue" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  {iconsVisibility.clear &&
                    <Tooltip title={iconsTooltipText.clear} arrow disableHoverListener={isDisabled || iconsDisabled.clear} disableFocusListener={isDisabled || iconsDisabled.clear}>
                      <span>
                        <IconButton onClick={handleClearClick} disabled={isDisabled || iconsDisabled.clear}>
                          <ClearIcon color="ywButtonBlue" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={[
              {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)', // Material-UI default border color
                  },
                  '&:hover fieldset': {
                    borderColor: '#5A73EA', // Optional hover style
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5A73EA', // Optional focus style
                  },
                },
                '& .MuiOutlinedInput-root.Mui-disabled': {
                    '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.12)', // Disabled state uses lighter gray
                    },
                },
                // Target the input element inside the TextField.
                '& .MuiInputBase-input': {
                  // For autofill in Chrome, Edge and Safari
                  '&:-webkit-autofill': {
                    // Apply a large inset box-shadow to override the browser's background
                    boxShadow: '0 0 0 1000px white inset !important',
                    // Ensure the text color stays as desired
                    WebkitTextFillColor: 'black !important',
                    // Optionally, you can add a long-duration transition to smooth out the autofill background removal.
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                },
              },
              sx,
            ]}
          />
          <label className={`${requiredFieldLabel !== "" ? "requiredFieldLabel" : ""}`}>
            <span style={{ display: 'inline-block', textAlign: 'center', color: 'red' }}>
              {requiredFieldLabel}
            </span>
          </label>
        </div>
        {mainValidationMessage && (
          <span style={{ color: "red", fontSize: "12px", marginTop: "5px", display: "block" }}>
            {mainValidationMessage}
          </span>
        )}
      </span>
    </div>
  );

  /* 
    // Code to be used in Parent When value is controlled internally
    
    const searchFieldRef = useRef();
  
    const handleGetValue = () => {
      const currentValue = searchFieldRef.current.getTextValue();
      console.log("Retrieved value from SearchField:", currentValue);
    };
  
    const handleSetValue = () => {
      searchFieldRef.current.setTextValue("New Value");
    }; */


}));


const DebounceForm = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const ValidateRequiredFieldsInForm = (values, rules, formState = null) => {
  let isFormValid = true; // Initialize the form validity flag
  let requiredValueChanged = false;

  const validationResults = {
    validationLabels: Object.keys(rules).reduce((result, field) => {
      const rule = rules[field];
      let isValid = true;
      let text = "";

      if (rule.required) {
        isValid = rule.validator ? rule.validator(values[field], values) : Boolean(values[field]);
        
        // Check if the field is disabled in formState, if so don't show asterisk
        const isFieldEnabled = formState ? formState[field] !== false : true;
        text = (isValid || !isFieldEnabled) ? "" : "*";

        // Update the form validity flag only for enabled fields
        if (!isValid && isFieldEnabled) {
          isFormValid = false;
        }

        // To check if atleast one of the value of a required field in the form has changed
        if (rule.requireValueChange) {
          if (isValid) {
            if (rule.valueChangeCheck) {
              const isValidValue = rule.valueChangeCheck ? rule.valueChangeCheck(values[field], values) : Boolean(values[field]);
              if (isValidValue) {
                requiredValueChanged = true;
              }
            }
            else {
              requiredValueChanged = true;
            }
          }
        }
      }

      result[field] = text;
      return result;
    }, {})
  };

  // Add the form validity flag to the result
  return { ...validationResults, isFormValid, requiredValueChanged };
};

const ValidateForm = (values, rules) => {
  return Object.keys(rules).every((field) => {
    const rule = rules[field];
    if (rule.required) {
      return rule.validator ? rule.validator(values[field]) : Boolean(values[field]);
    }
    return true; // Not required
  });
};

let dialogThemeColor = "ywNotificationNormal";

const getIconByType = (type) => {
  switch (type) {

    case EnumUtility.NotificationType.Success:
      dialogThemeColor = "ywNotificationSuccess";
      return <CheckCircleIcon className="yt-dialog-icon" style={styles.iconStyle} />;

    case EnumUtility.NotificationType.Error:
      dialogThemeColor = "ywNotificationError";
      return <ErrorIcon className="yt-dialog-icon" style={styles.iconStyle} />;

    case EnumUtility.NotificationType.Notification:
      dialogThemeColor = "ywNotificationNormal";
      return <InfoIcon className="yt-dialog-icon" style={styles.iconStyle} />;

    case EnumUtility.NotificationType.Warning:
      dialogThemeColor = "ywNotificationWarning";
      return <WarningIcon className="yt-dialog-icon" style={styles.iconStyle} />;

    default:
      return null;
  }
};

const ConfirmationDialog = ({
  openConfirmation,
  dialogContent,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  messageType,
  onCloseConfirmation,
  onConfirmDialog,
}) => {

  let msgType;
  let bgColor;
  let iconType;
  let dialogTitle;
  let dialogMessage;
  let dialogID = 0;

  if (dialogContent) {
    msgType = dialogContent.NotificationType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = dialogContent.Title;
    dialogMessage = dialogContent.Message;
    dialogID = dialogContent.ID;
  }
  else {
    msgType = messageType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = title;
    dialogMessage = message;
  }

  const handleConfirmDialogClick = () => {
    onConfirmDialog(dialogID);
  };

  const shouldPreventBackdropDismissal = msgType === EnumUtility.NotificationType.Warning || 
                                        msgType === EnumUtility.NotificationType.Error;
  return (
    <Dialog 
      open={openConfirmation} 
      onClose={shouldPreventBackdropDismissal ? undefined : onCloseConfirmation} 
      classes={{ paper: bgColor }}
      disableEscapeKeyDown={shouldPreventBackdropDismissal}
      disableBackdropClick={shouldPreventBackdropDismissal}
      PaperProps={{
        sx: {
          minWidth: 700,
          maxWidth: "90%", // Prevents exceeding viewport width
          margin: "auto" 
        },
      }}>
      <DialogContent
        sx={{
          padding: "5px"
        }}>
        <div style={styles.dialogContent}>
          <div style={styles.iconColumn}>
            {iconType}
          </div>
          <div style={styles.textColumn}>
            <p style={styles.title}>{dialogTitle}</p>
            <Typography component="div" style={styles.message}><pre className="text-dialog-message" style={styles.message}>{dialogMessage}</pre></Typography>
          </div>
          <div style={styles.iconColumn}>
            <IconButton className="yt-dialog-close" onClick={onCloseConfirmation}>
              <Close style={styles.iconStyle} />
            </IconButton>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div style={styles.actions}>
          <Button variant="contained" color={dialogThemeColor} className='me-2' onClick={handleConfirmDialogClick}>
            Yes
          </Button>
          <Button variant="contained" color={dialogThemeColor} className='me-2' onClick={onCloseConfirmation}>
            No
          </Button>
        </div>
      </DialogActions>
    </Dialog>

  );
};

const NotificationDialog = ({
  openNotification,
  dialogContent,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  dynamicMessageContent = [],
  onCloseNotification,
  messageType,
  onConfirm,
  isAutoClose = false,
  timerDuration = 0,
  position = "center",
  backdropDismissal = false
}) => {

  const timerRef = useRef(null); // Ref to store the timer ID

  useEffect(() => {
    if (openNotification && isAutoClose && timerDuration > 0) {
      // Clear any existing timer before setting a new one
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set a new timer
      timerRef.current = setTimeout(() => {
        onCloseNotification(); // Close the dialog after the timer
      }, timerDuration);
    }
    
    // Cleanup function to clear the timer
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      } 
    };

  }, [openNotification, isAutoClose, timerDuration, onCloseNotification]);

  let msgType;
  let bgColor;
  let iconType;
  let dialogTitle;
  let dialogMessage;
  let isHtmlContent = false;

  if(dialogContent){
    msgType = dialogContent.NotificationType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = dialogContent.Title;
    isHtmlContent = dialogContent.isHtmlContent || false;
    const dynamicMessageContent = dialogContent.dynamicMessageContent ? dialogContent.dynamicMessageContent : [];
    if(dynamicMessageContent.length > 0) {
      dialogMessage = UtilityLibrary.formatString(dialogContent.Message, dynamicMessageContent);
    }
    else {
      dialogMessage = dialogContent.Message;
    }    
  }
  else{
    msgType = messageType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = title;
    dialogMessage = message;
  }

  // Determine if dialog should prevent backdrop/ESC key dismissal
  // Warning and Error types require user action - prevent dismissal via backdrop/ESC
  // Success types can be dismissed by clicking outside. 
  // Backdrop/ESC key dismissal was enabled for error in task #57164
  const shouldPreventBackdropDismissal = (msgType === EnumUtility.NotificationType.Warning || 
                                        msgType === EnumUtility.NotificationType.Error) && !backdropDismissal;

  const handleOk = () => {
    onConfirm?.();            // logic 
    onCloseNotification();  // always close UI
  };
  return (

    <Dialog 
      open={openNotification} 
      onClose={shouldPreventBackdropDismissal ? undefined : onCloseNotification} 
      classes={{ paper: bgColor }}
      disableEscapeKeyDown={shouldPreventBackdropDismissal}
      disableBackdropClick={shouldPreventBackdropDismissal}
      PaperProps={{
  sx: {
    position: "fixed",
    width: "auto",
    maxWidth: "calc(100vw - 32px)",
    m: 0,
    p: 0,
    overflowX: "hidden",
    zIndex: 1000,

    top:
      position === "center"
        ? "50%"
        : position === "right"
        ? "50%"
        : position === "top-right"
        ? "30%"
        : "auto",

    left:
      position === "center"
        ? "50%"
        : position === "right"
        ? "80%"
        : position === "top-right"
        ? "80%" 
        : "auto",

    right: "auto",

    transform:
      position === "center"
        ? "translate(-50%, -50%)"
        : position === "right"
        ? "translate(-50%, -50%)"
        : position === "top-right"
        ? "translateX(-50%)"
        : "none",
  }
}}>
      <DialogContent
        sx={{
          padding: "5px"
        }}>
        <div style={styles.dialogContent}>
          <div style={styles.iconColumn}>
            {iconType}
          </div>
          <div style={styles.textColumn}>
            <p style={styles.title}>{dialogTitle}</p>
            {isHtmlContent ? (
              <Typography component="div" style={styles.message}>
                <div 
                  className="text-dialog-message" 
                  style={styles.message} 
                  dangerouslySetInnerHTML={{ __html: dialogMessage }}
                />
              </Typography>
            ) : (
              <Typography component="div" className="text-dialog-message" style={styles.message}>
                {dialogMessage}
              </Typography>
            )}
          </div>
          {!isAutoClose && <div style={styles.iconColumn}>
            <Button variant="contained" color={dialogThemeColor} onClick={handleOk}>
              OK
            </Button>
          </div>}
        </div>
      </DialogContent>
      {/* <DialogActions>//Removed it as it was not being used
        <div style={styles.actions}>
        </div>
      </DialogActions> */}
    </Dialog> 

  );
};

const CommonDialog = ({
  openDialog,
  onCloseDialog,
  title,
  children,
  contentDialog,
  contentProps = {},
  maxWidth = 'md',
  minHeightCustom = 200,
  minWidthCustom = 700,
  fullWidth = false
}) => {
  return (
    <Dialog
      open={openDialog}
      onClose={onCloseDialog}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      classes={{ paper: 'yt-dialog-main' }}
      PaperProps={{
        sx: {
          minWidth: 700,
          maxWidth: "90%", // Prevents exceeding viewport width
          margin: "auto" // Set the minimum width
        },
      }}
    >
      <DialogContent
        sx={{
          padding: "5px"
        }}>
        <div className="pane-main-container">
          <DialogTitle>
            {title}
            <IconButton
              aria-label="close"
              onClick={onCloseDialog}
              sx={{
                position: 'absolute',
                right: 25,
                top: 25,
                color: 'ywButtonBlue',
              }}
            >
              <Close color="ywButtonBlue" />
            </IconButton>
          </DialogTitle>
          <DialogContent className="common-dialog-content" dividers
            sx={{
              minHeight: minHeightCustom,
            }}>
            {contentDialog}
          </DialogContent>

        </div>
      </DialogContent>
    </Dialog>
  );
};


const MessageBox = ({
  messageContent,
  title = "",
  message = "",
  messageType,
  isAutoClose = false,
  timerDuration = 0
}) => {

  let msgType;
  let bgColor;
  let iconType;
  let dialogTitle;
  let dialogMessage;

  if (messageContent) {
    msgType = messageContent.NotificationType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = messageContent.Title;
    dialogMessage = messageContent.Message;
  }
  else {
    msgType = messageType;
    bgColor = msgType ? `yt-dialog-${msgType}` : "yt-dialog-root";
    iconType = getIconByType(msgType);
    dialogTitle = title;
    dialogMessage = message;
  }

  return (
    <div className={`${bgColor}`}>
      <div style={styles.dialogContent}>
        <div style={styles.iconColumn}>
          {iconType}
        </div>
        <div style={styles.textColumn}>
          <p style={styles.title}>{dialogTitle}</p>
          <Typography component="div" style={styles.message}><pre className="text-dialog-message" style={styles.message}>{dialogMessage}</pre></Typography>
        </div>
      </div>
    </div>
  );
};


const PaperComponent = React.forwardRef(function CustomPaper(props, ref) {
  const { style, ...other } = props;
  const nodeRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Mouse event handlers
  const handleMouseDown = (e) => {
    // Only drag with left mouse button
    if (e.button !== 0) return;
    setDragging(true);
    const rect = nodeRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  });

  // Merge custom position with style
  const mergedStyle = {
    ...style,
    position: 'fixed',
    left: position.x || '50%',
    top: position.y || '50%',
    transform: position.x || position.y ? 'none' : 'translate(-50%, -50%)',
    margin: 0,
    zIndex: 1300,
    resize: 'both',
    overflow: 'hidden',
  };

  // Attach mouse down to header only
  useEffect(() => {
    const header = nodeRef.current?.querySelector('#draggable-dialog-title');
    if (header) {
      header.style.cursor = 'move';
      header.addEventListener('mousedown', handleMouseDown);
      return () => header.removeEventListener('mousedown', handleMouseDown);
    }
  });

  return (
    <Paper ref={nodeRef} style={mergedStyle} {...other} />
  );
});

const HtmlContentDialog = ({
  openDialog,
  onCloseDialog,
  title = "Information",
  htmlContent = "",
  maxWidth = 'md',
  minHeight = 300,
  maxHeight = 600
}) => {
  const contentRef = useRef();

  return (
    <Dialog
      open={openDialog}
      onClose={onCloseDialog}
      maxWidth={false}
      PaperComponent={PaperComponent}
      PaperProps={{
        sx: {
          width: 700,
          minHeight: 200,
          maxHeight: '85vh',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          m: 0,
          p: '24px 24px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #ddd',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: '70px',
          fontSize: '20px',
          fontWeight: 500,
          color: '#555',
          position: 'relative',
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={onCloseDialog}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999',
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '20px',
          backgroundColor: '#fff',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'auto',
        }}
      >
        <div
          ref={contentRef}
          style={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            height: 'auto',
            padding: 0,
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#333',
            boxSizing: 'border-box',
            whiteSpace: 'pre-line',
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </DialogContent>
      <DialogActions sx={{
        padding: '12px 24px',
        backgroundColor: '#fff',
        flexShrink: 0
      }}>
        <Button
          onClick={onCloseDialog}
          variant="text"
          color="ywTextBlue"
        >
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CollapsibleSection = ({ titleSection, children }) => {
  const [isExpandContent, setIsExpandContent] = useState(false);
  const contentRef = useRef(null);

  return (
    <div className="collapsible-container">
      <div className="collapsible-header" onClick={() => setIsExpandContent(!isExpandContent)}>
        <div className="collapsible-header-left-column text-wrapper-subtitle">
          {titleSection}
        </div>
        <div className="collapsible-header-right-column">
          {isExpandContent ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </div>
      </div>
      <div
        className="collapsible-content"
        ref={contentRef}
        style={{
          maxHeight: isExpandContent ? contentRef.current?.scrollHeight : 0,
        }}
      >
        <div className="collapsible-body">{children}</div>
      </div>
    </div>
  );
};

const CustomAutoComplete = ({
  idForControl,
  labelForControl,
  placeholder,
  valueForControl,
  onChange,
  optionsSource,
  debounceDelay = 300,
  labelKey = 'label', // default to 'label'
  valueKey = 'value', // default to 'value'
  getOptionLabel,
  isOptionEqualToValue = (option, value) => option?.value === value?.value,
  noOptionsText = 'No values found',
  isDisabled = false,
  customVariant = "defaultVariant",
  allowClear = true,
  multiple = true,
  disableCloseOnSelect = true,
  renderTags,
  renderOption,
  open,
  onOpen,
  onClose,
  slotProps,
}) => {
  const computedGetOptionLabel = getOptionLabel || ((option) => option?.[labelKey] ?? '');

  const computedIsOptionEqualToValue =
    isOptionEqualToValue || ((option, value) => option?.[valueKey] === value?.[valueKey]);

  const selectedValue = multiple
    ? optionsSource.filter((opt) => (Array.isArray(valueForControl) ? valueForControl : []).includes(opt?.[valueKey]))
    : optionsSource.find((opt) => opt?.[valueKey] === valueForControl) || null;

  return (
    <FormControl fullWidth>
      <Autocomplete
        variant={customVariant}
        multiple={multiple}
        disableCloseOnSelect={disableCloseOnSelect}
        filterSelectedOptions={false}
        disableClearable={!allowClear}
        options={optionsSource}
        getOptionLabel={computedGetOptionLabel}
        isOptionEqualToValue={computedIsOptionEqualToValue}

        id={idForControl}
        name={idForControl}
        value={selectedValue}
        IconComponent={ArrowDropDown}
        label={labelForControl}
        noOptionsText={noOptionsText}
        displayEmpty

        disabled={isDisabled}

        renderTags={renderTags} 
        renderOption={renderOption} // Add this line

        onChange={(event, newValue, reason, details) => onChange(event, newValue, reason, details)}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        slotProps={slotProps}

        renderInput={(params) => (
          <TextField {...params}
            label={labelForControl}
            variant="outlined"
            InputLabelProps={{
              shrink: true, // Always keeps the label shrunk
            }}
          />
        )}
      >
      </Autocomplete>
    </FormControl>
  );
};


const styles = {
  dialogContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    color: '#FFF',
    gap: '10px',
    maxWidth: '100%'
  },
  iconColumn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flex: '0 0 auto',
  },
  textColumn: {
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'left',
  },
  iconStyle: {
    fontSize: '2rem',
  },
  title: {
    fontSize: '1rem',
  },
  message: {
    fontSize: '1rem',
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    margin: 0
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    gap: '45px'
  },
 
  actionCol1: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    gap: '45px'
  },
  actionCol2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    gap: '45px'
  }
  
};

const TextFieldDefault = styled(TextField)({
  '& .MuiOutlinedInput-input': {
    padding: '10px',
  },
  '&:hover fieldset': {
    borderColor: '#5A73EA', // hover border color
  },
  '& .MuiOutlinedInput-root': { // For BORDER around TextField
    '&.Mui-focused fieldset': {
      color: '#5A73EA',
      borderColor: '#5A73EA', 
    },
  },
  '& .MuiInputLabel-root': { // For Label in TextField
    '&.Mui-focused': {
      color: '#5A73EA',
    },
  },
});

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.ywMainBlue.main, // Unchecked border color
  '&.Mui-checked': {
    color: theme.palette.ywMainBlue.main, // Checked color
  },
}));


const SearchTextFieldCustom = forwardRef(({
  valueForField: externalValue,
  nameForField = "",
  onIconButtonClick,        // Custom button click handler passed from the parent
  onClearButtonClick,        // Custom button click handler passed from the parent to clear the text
  onTextChange,
  textIcon,            // Icon passed from the parent (default is SearchIcon)
  labelForField = "Search", // Label for the textfield
  placeholder = "Search", // Placeholder for the textfield
  initialText = "", // Initial Text value
  isDisabled = false,
  iconsVisibility = { search: true, copy: false, clear: true },
  iconsDisabled = { search: false, copy: true, clear: false },
  iconsTooltipText = { search: 'Search', copy: 'Copy Text', clear: 'Clear Text' },
  requiredFieldLabel = ""
}, ref) => {

  const [internalValue, setInternalValue] = useState(""); // Internal state for default behavior

  const [mainValidationMessage, setMainValidationMessage] = useState("");

  // Determine whether the field is external or internal
  const isExternal = externalValue !== undefined;

  // Handle input change
  const handleTextChange = (e) => {
    if (onTextChange) {
      onTextChange(e); // If parent provided an onChange, invoke it
    } else {
      setInternalValue(e.target.value); // Update internal state 
    }
  };

  const handleButtonClick = (event) => {
    const searchValue = isExternal ? externalValue : internalValue; // Get value based on mode
    if (searchValue.trim()) {
      if (onIconButtonClick) {
        onIconButtonClick(event); // Calls the parent-provided search handler
      } else {
        // Default search action
      }
    }
  };

  // Handle Clear Icon Click
  const handleClearClick = (e) => {
    if (onClearButtonClick) {
      setInternalValue(""); // Update internal state 
      onClearButtonClick(e); // If parent provided an onClearButtonClick, invoke it      
    } else {
      setInternalValue(""); // Update internal state 
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleButtonClick();
    }
  };

  // Handle Copy Icon Click
  const handleCopyTextClick = async (e) => {
    try {
      const textToCopy = isExternal ? externalValue : internalValue;
      await navigator.clipboard.writeText(textToCopy);
    }
    catch (error) {

    }
  };

  const clearAllText = () => {
    externalValue = "";
    setInternalValue("");
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getTextValue: () => (isExternal ? externalValue : internalValue), // Get current value
    setTextValue: (newValue) =>
      isExternal ? null : setInternalValue(newValue), // Set value (only in internal mode)
    setValidationMessage: (newValue) =>
      setMainValidationMessage(newValue), // Set validation message (only in internal mode)
    clearAllText,
  }));

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <span>
        <div className={`textFieldWithIcon ${requiredFieldLabel !== "" ? "textFieldWithIconRequired" : ""}`}>
          <TextField
            size="small"
            label={labelForField}
            name={nameForField}
            placeholder={placeholder}
            variant="outlined"
            value={isExternal ? externalValue : internalValue}
            disabled={isDisabled}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {iconsVisibility.search &&
                    <Tooltip title={iconsTooltipText.search} arrow disableHoverListener={isDisabled || iconsDisabled.search} disableFocusListener={isDisabled || iconsDisabled.search}>
                      <span>
                        <IconButton onClick={handleButtonClick} style={{ padding: 10 }} disabled={isDisabled || iconsDisabled.search}>
                          {textIcon || <SearchIcon color="ywButtonBlue" />} {/* Default to SearchIcon if no icon is provided */}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  {iconsVisibility.copy &&
                    <Tooltip title={iconsTooltipText.copy} arrow disableHoverListener={isDisabled || iconsDisabled.copy} disableFocusListener={isDisabled || iconsDisabled.copy}>
                      <span>
                        <IconButton onClick={handleCopyTextClick} disabled={isDisabled || iconsDisabled.copy}>
                          <ContentCopyIcon color="ywButtonBlue" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  {iconsVisibility.clear &&
                    <Tooltip title={iconsTooltipText.clear} arrow disableHoverListener={isDisabled || iconsDisabled.clear} disableFocusListener={isDisabled || iconsDisabled.clear}>
                      <span>
                        <IconButton onClick={handleClearClick} disabled={isDisabled || iconsDisabled.clear}>
                          <ClearIcon color="ywButtonBlue" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#5A73EA', // Change border color based on value
                },
                '&:hover fieldset': {
                  borderColor: '#5A73EA', // Optional hover style
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#5A73EA', // Optional focus style
                },
              },

              '& label.Mui-focused': {
                color: '#5A73EA',
                alignItems: 'center',
              },
              '& .MuiOutlinedInput-root.Mui-disabled': {
                '& fieldset': {
                  borderColor: '#5A73EA', // Change border color based on value
                },
              },
              // Target the input element inside the TextField.
              '& .MuiInputBase-input': {
                // For autofill in Chrome, Edge and Safari
                '&:-webkit-autofill': {
                  // Apply a large inset box-shadow to override the browser�s background
                  boxShadow: '0 0 0 1000px white inset !important',
                  // Ensure the text color stays as desired
                  WebkitTextFillColor: 'black !important',
                  // Optionally, you can add a long-duration transition to smooth out the autofill background removal.
                  transition: 'background-color 5000s ease-in-out 0s',
                },
              },
            }}
          />
          <label className={`${requiredFieldLabel !== "" ? "requiredFieldLabel" : ""}`}>
            <span style={{ display: 'inline-block', textAlign: 'center', color: 'red' }}>
              {requiredFieldLabel}
            </span>
          </label>
        </div>
        {mainValidationMessage && (
          <span style={{ color: "red", fontSize: "12px", marginTop: "5px", display: "block" }}>
            {mainValidationMessage}
          </span>
        )}
      </span>
    </div>
  );

});

const RoundedSquareRefreshButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.ywMainBlue.main,
  borderRadius: '16%', // Adjust this value for more or less rounding
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.ywMainBlue.main,
  },
}));

// White RefreshIcon component
const WhiteRefreshIcon = styled(AutorenewIcon)({
  color: '#fff',
});

const caseSearchDialogStyles = {
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
        maxWidth: '100%'
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        padding: '16px'
    },
    caseInfoRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '10px',
        padding: '10px 16px 0',
        width: '100%',
        boxSizing: 'border-box'
    },
    caseInfoFieldFullRow: {
      padding: '16px 5px 0',
      display: 'flex',  
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: '4px',
      gridColumn: '1 / -1',
      width: '100%',
      flexWrap: 'nowrap'
    },
    caseInfoField: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',/* Align label and input vertically */
        gap: '4px', /* Space between label and input */
        flexWrap: 'nowrap' /* Ensure label and input stay on the same row */
    },
    caseInfoTitle:{
        fontSize: '1rem',
        flex: '0 0 auto',
        fontWeight: 'bold',
    },
    caseInfoContent: {
        fontSize: '1rem',
        flex: '1 1 auto',
        fontWeight: 'bold',
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
        padding: '20px 16px',
        gap: '10px'
    },

    actionCol1: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    actionCol2: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
        gap: '5px'
    }

};
const CaseSearchDialog = ({
  openDialogBox,
  dialogContent,
  onCloseDialog,
  onConfirmDialog,
  validateEmails,
  onClearDialog,
  onResetDone,
  onCaseSearch,
  caseNumber,
  onCaseChange,
  onApplyCase,
  onProceedWithoutCase,
  caseSearchComplete,
  caseNumberRegex,
  caseData,
  isSupRequired,
  stateID,
  noCase
}) => {
    console.log("INSIDE CASE SEARCH DIALOG", openDialogBox, isSupRequired, stateID);

    const caseNumberRef = useRef();
    const errorFormName = "Correct Case Search";
    const canApplyCase = !!caseData && !!caseSearchComplete;
    const canProceedWthoutCase = !(caseData || caseNumber);

    const [selectedDocGroup, setSelectedDocGroup] = useState(null);
    const [selectedDocType, setSelectedDocType] = useState(null);
    const [allDocGroups, setAllDocGroups] = useState([]);
    // Flattened document type data
    const [documentTypeList, setDocumentTypeList] = useState([]);
    // Document Types currently displayed
  const [documentTypes, setDocumentTypes] = useState([]);
  const [exclusionGroup, setExclusionGroup] = useState(null);
    //A searched case overrides the parent's noCase value.
    const dialogNoCase = caseData ? false : noCase;

  const docGroupsWithCase = useMemo(() => {
      if (!isSupRequired) {
        return [];
      }
      const filteredData = UtilityLibrary.filterArrayByCondition(allDocGroups, item => item.caseNoCaseFlag === 2 || item.caseNoCaseFlag === 3);
      const formatted = filteredData.map(item => ({id: item.documentGroupTypeID, name: item.documentGroupTypeName})).sort((a, b) =>a.name.localeCompare(b.name));
      // All
      return formatted.length ? [{ id: 0, name: "All" }, ...formatted]: [];
    }, [allDocGroups, isSupRequired]);

  const docGroupsWithNoCase = useMemo(() => {
    if (!isSupRequired) {
        return [];
    }
    const filteredData = UtilityLibrary.filterArrayByCondition(allDocGroups, item => (item.caseNoCaseFlag === 1 || item.caseNoCaseFlag === 3)
      && item.documentGroupTypeID !== exclusionGroup);
    const formatted = filteredData.map(item => ({ id: item.documentGroupTypeID, name: item.documentGroupTypeName })).sort((a, b) => a.name.localeCompare(b.name));
    return formatted.length ? [{ id: 0, name: "All" }, ...formatted]: [];
  }, [allDocGroups, isSupRequired]);


  const activeDocGroups = dialogNoCase ? docGroupsWithNoCase: docGroupsWithCase;

  useEffect(() => {
    const fetchDocGroups = async () => {
      try {
        // Get exclusion Doc Group
         const params = {
          settingsDetailIDList: String(ClientType.SettingsDetail.UI_Miscellaneous__Doc_Group_Exclusion_For_Workflow_Reindex),
          yTracSiteID: stateID,
        };
        const settingDetail = await ApiManager.GetSettingsDetailValuesFromApi(params);
        const exclusionGroupID = Number(settingDetail?.settingDetailsApiData?.[0]?.settingsDetailValue ?? 0);
        setExclusionGroup(exclusionGroupID);
        // Get Document Groups
          const response = await ApiManager.GetDocGroupsFromApi({clientID: stateID});
          const groups = response?.[0] ?? [];
          setAllDocGroups(groups);
          // Flatten all document types.
          const allDocumentTypes = groups.flatMap(group => group.documentTypes || []).filter(item => item.documentGroupTypeID !== exclusionGroup);
          const formattedDocumentTypes =
              allDocumentTypes.map(item => ({documentTypeID: item.documentTypeID,
                  documentTypeName: item.documentTypeName,
                  documentGroupTypeID: item.documentGroupTypeID,
                  dT_CaseNoCaseFlag: item.dT_CaseNoCaseFlag,
                  displayOrder: item.displayOrder
              }));
        setDocumentTypeList(formattedDocumentTypes);
       
      } catch (error) {
          setAllDocGroups([]);
          setDocumentTypeList([]);
          setDocumentTypes([]);
          setSelectedDocGroup(null);
          setSelectedDocType(null);

        await LoggerUtility.ManageErrorLogging(error, errorFormName,"fetchDocGroups");
      }
    };

    if (stateID && isSupRequired) {
        fetchDocGroups();
    }
  }, [stateID, isSupRequired, openDialogBox]);

const loadDocumentTypesByGroupForNoCase = async (groupID) => {
  try {
    if (groupID === 0) {
      const condition = (item) => item.dT_CaseNoCaseFlag === 1 || item.dT_CaseNoCaseFlag === 3;
      const documentTypes = UtilityLibrary.filterArrayByCondition(documentTypeList, condition);
      return documentTypes.map((item) => ({
          id: item.documentTypeID,
          name: item.documentTypeName,
          displayOrder: item.displayOrder,
      }));
    }
    //Specific No-Case document group
    const selectedGroup = documentTypeList.filter( item => item.documentGroupTypeID === groupID);
    const condition = (item) => item.dT_CaseNoCaseFlag === 1 || item.dT_CaseNoCaseFlag === 3;
    const loadTypesByGroup = UtilityLibrary.filterArrayByCondition(selectedGroup, condition);
    return loadTypesByGroup.map((item) => ({
        id: item.documentTypeID,
        name: item.documentTypeName,
        displayOrder: item.displayOrder,
    }));
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, errorFormName, "loadDocumentTypesByGroupForNoCase");
    return [];
  }
};
  
   //Load document types of case
  const loadCaseDocumentTypes = (groupID) => {
    let filteredTypes = [];
    // ALL
    if (groupID === 0) { filteredTypes = documentTypeList.filter(item => item.dT_CaseNoCaseFlag === 2 || item.dT_CaseNoCaseFlag === 3); }
    // Specific case group
    else {
      filteredTypes =documentTypeList.filter(item => item.documentGroupTypeID === groupID && (item.dT_CaseNoCaseFlag === 2 || item.dT_CaseNoCaseFlag === 3));
    }
    const formattedData =
      filteredTypes.map(item => ({
        id: item.documentTypeID,
        name: item.documentTypeName,
        displayOrder: item.displayOrder,
       groupID: item.documentGroupTypeID
      })).sort((a, b) => a.name.localeCompare(b.name));

    setDocumentTypes(formattedData);
    // If only one doc type auto select Document Type
    if (formattedData.length === 1) {
      setSelectedDocType(formattedData[0].id);
    } else {
      setSelectedDocType(null);
    }
    return formattedData;
  };

  const loadNoCaseDocumentTypes = async (groupID) => {
    try {
      const formattedData = await loadDocumentTypesByGroupForNoCase(groupID);
      const sortedData = formattedData ? [...formattedData].sort((a, b) => a.name.localeCompare(b.name)) : [];
      setDocumentTypes(sortedData);
      // For single document type, auto select Document Type
      if (sortedData.length === 1) {
        setSelectedDocType(sortedData[0].id);
      } else {
        setSelectedDocType(null);
      }
      return sortedData;
    } catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "loadNoCaseDocumentTypes");
      setDocumentTypes([]);
      setSelectedDocType(null);
      return [];
    }
  }

    useEffect(() => {
      if (!openDialogBox || !isSupRequired || !allDocGroups.length) {return;}
      const initializeSelection = async () => {
          try {
            // No Case
            if (dialogNoCase) {
              if (docGroupsWithNoCase.length ===0) {
                setSelectedDocGroup(null);
                setDocumentTypes([]);
                setSelectedDocType(null);

                return;
              }
              // Single group, auto-select it and load document types.
              if (docGroupsWithNoCase.length === 1) {
                const group = docGroupsWithNoCase[0];
                setSelectedDocGroup(group.id);
                await loadNoCaseDocumentTypes(group.id);
                  return;
              }
              // default to All
                setSelectedDocGroup(0);
                await loadNoCaseDocumentTypes(0);
                return;
              }
              // With Case
              if (docGroupsWithCase.length ===0) {
                  setSelectedDocGroup(null);
                  setDocumentTypes([]);
                  setSelectedDocType(null);
                  return;
              }
              const actualCaseGroups = docGroupsWithCase.filter(group => group.id !== 0);
              if (actualCaseGroups.length === 1) {
                const group = actualCaseGroups[0];
                setSelectedDocGroup(group.id);
                loadCaseDocumentTypes(group.id);
                return;
              }
              setSelectedDocGroup(0);
              loadCaseDocumentTypes(0);
          } catch (error) {
            await LoggerUtility.ManageErrorLogging( error, errorFormName, "initializeSelection");
          }
        };
        initializeSelection();
    }, [openDialogBox, dialogNoCase, allDocGroups, docGroupsWithCase, docGroupsWithNoCase, isSupRequired]);

    const handleGroupChange = async (event, newValue, reason) => {
      try {
          setSelectedDocType(null);
          if (reason === "clear" ||!newValue) {
            setSelectedDocGroup(null);
            setDocumentTypes([]);
            return;
          }
          const groupID = newValue.id;
          setSelectedDocGroup(groupID);
          // With Case
          if (!dialogNoCase) {
              loadCaseDocumentTypes(groupID);
              return;
          }
          // No case
          await loadNoCaseDocumentTypes(groupID);
      } catch (error) {
        await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleGroupChange");
      }
    };

    const handleDocTypeChange = (event, newValue, reason) => {
      if (reason === "clear" || !newValue ) {
        setSelectedDocType(null);
        return;
      }
      setSelectedDocType(newValue.id);
    };

    const validateCaseNumber = value => {
        const pattern = caseNumberRegex?.caseRegexValue || "";
        if (!pattern || !value) {
            return {isValid: true, message: ""};
        }
        const isValid = new RegExp(pattern).test(value);
        return {isValid, message: isValid ? "" : (caseNumberRegex?.caseRegexMessage || "")};
    };

    const handleCaseSearch = () => {
        const value = (caseNumber ?? "").trim();
        const { isValid, message} = validateCaseNumber(value);
        caseNumberRef.current?.setValidationMessage(message);
        if (value && isValid) {
          //dialogNoCase: true -> false
          setSelectedDocGroup(0);
          setSelectedDocType(null);
          onCaseSearch?.(caseNumber);
        }
    };

    const handleCaseChange = e => {
        const value = e?.target?.value ?? "";
        const { message} = validateCaseNumber(value);
        caseNumberRef.current?.setValidationMessage(
          message
        );
        onCaseChange?.(value);
  };
  

  const handleApplyCase = () => {
    const selectedDocTypeData = documentTypes.find(
        docType => docType.id === selectedDocType
    );
    const selectedDocGroupData = activeDocGroups.find(
        group => group.id === selectedDocTypeData.groupID
    );
    onApplyCase(caseNumber, selectedDocGroupData, selectedDocTypeData);
  }

  const handleProceedWithoutCase = () => {
    const selectedDocGroupData = activeDocGroups.find( group => group.id === selectedDocGroup
    );
    const selectedDocTypeData = documentTypes.find( docType => docType.id === selectedDocType
    );
    onProceedWithoutCase(selectedDocGroupData, selectedDocTypeData);
  }

    useEffect(() => {
        if (!openDialogBox) {caseNumberRef.current?.setValidationMessage("");}
      }, [openDialogBox]);

    return (
        <Dialog
          open={openDialogBox}
          onClose={onCloseDialog}
          PaperProps={{
            sx: {
                minWidth: 300,
                maxWidth: "md",
                width: "90%",
                position: "fixed",
                zIndex: 1000
            }
          }}
        >
          <DialogTitle sx={{padding: "16px 16px 0"}}>
            <div style={{display: "flex", justifyContent: "space-between",alignItems: "center"}}>
                <Typography style={caseSearchDialogStyles.title}>
                    Search For Correct Case
                </Typography>
                <IconButton onClick={onCloseDialog}>
                  <Close />
                </IconButton>
            </div>
          </DialogTitle>

          <DialogContent sx={{mt: 2}}>
            <div style={{padding: "16px", width: "100%"}}>
              <div>
                  <TextFieldWithIconButton
                      ref={caseNumberRef}
                      nameForField="caseNumberTxt"
                      onIconButtonClick={handleCaseSearch}
                      onTextChange={handleCaseChange}
                      valueForField={caseNumber}
                      labelForField="Case Search"
                      iconsTooltipText={{
                        search: "Search Case",
                        copy: "Copy Case",
                        clear: "Clear Case"
                      }}
                  />
              </div>
              <div style={caseSearchDialogStyles.caseInfoRow}>
                  <div style={caseSearchDialogStyles.caseInfoField}>
                      <label style={caseSearchDialogStyles.caseInfoTitle}>
                          NCP Name:
                      </label>

                      <label id="ncpNameLbl" name="ncpNameLbl" style={caseSearchDialogStyles.caseInfoContent}>
                          {caseData?.ncpFullName}
                      </label>
                  </div>
                  <div style={caseSearchDialogStyles.caseInfoField}>
                      <label style={caseSearchDialogStyles.caseInfoTitle}>
                          CP Name:
                      </label>

                      <label id="cpNameLbl" name="cpNameLbl" style={caseSearchDialogStyles.caseInfoContent}>
                          {caseData?.cpFullName}
                      </label>
                  </div>
              </div>
                {/* DOCUMENT GROUP */}
                {isSupRequired && (
                    <div
                        style={caseSearchDialogStyles.caseInfoFieldFullRow}>
                        <FormControl fullWidth>
                            <Autocomplete
                                options={activeDocGroups}
                                getOptionLabel={option => option.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                id="docGroupSelect"
                                value={
                                    activeDocGroups.find(opt => opt.id === selectedDocGroup) ||null
                                }
                                label="Document Group"
                                noOptionsText="Document Group not found"
                                onChange={( event, newValue, reason) =>
                                    handleGroupChange(event, newValue, reason)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Document Group"
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                        />
                                    )}
                                  />
                            </FormControl>
                      </div>
                )}
                {/* DOCUMENT TYPE */}
                {isSupRequired && (
                    <div
                        style={caseSearchDialogStyles.caseInfoFieldFullRow}>
                          <FormControl fullWidth>
                            <Autocomplete
                                options={documentTypes}
                                getOptionLabel={option => option.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                id="docTypeSelect"
                                value={
                                    documentTypes.find(opt => opt.id === selectedDocType) ||null
                                }
                                label="Document Type"
                                noOptionsText="Document Type not found"
                                onChange={( event, newValue, reason) =>
                                    handleDocTypeChange(event, newValue, reason)}
                                    renderInput={params => (
                                      <TextField
                                          {...params}
                                          label="Document Type"
                                          variant="outlined"
                                          InputLabelProps={{
                                              shrink: true
                                          }}
                                      />
                                    )}
                                  />
                           </FormControl>
                    </div>
                )}
              </div>
          </DialogContent>

          <DialogActions>
            <div style={{...caseSearchDialogStyles.actions}}>
            <div style={caseSearchDialogStyles.actionCol1}>
              <Button color="ywButtonWhite" variant="outlined" onClick={onCloseDialog}>
                  CANCEL
              </Button>
            </div>
            <div style={caseSearchDialogStyles.actionCol2}>
              <Button color="ywButtonBlack" variant="contained" disabled={!canApplyCase}
                onClick={() => handleApplyCase()}>
                  APPLY CASE
              </Button>

              <Button className="ywButtonBlue" variant="contained" disabled={!canProceedWthoutCase} onClick={() => handleProceedWithoutCase()}>
                  PROCEED WITHOUT CASE
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>
    );
};

export default React.memo(TextAreaWithCount);
export {
  TextFieldWithIconButton,
  DebounceForm,
  ValidateRequiredFieldsInForm,
  ValidateForm,
  ConfirmationDialog,
  NotificationDialog,
  CommonDialog,
  HtmlContentDialog,
  CaseSearchDialog,
  MessageBox,
  CollapsibleSection,
  CustomAutoComplete,
  TextFieldDefault,
  CustomCheckbox,
  SearchTextFieldCustom,

  RoundedSquareRefreshButton,
  WhiteRefreshIcon,
  
};
